import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';

/** A page's refresh handler — re-fetches that page's data. May be sync or async. */
type RefreshHandler = () => Promise<unknown> | void;

/**
 * Refresh context interface.
 *
 * The pull-to-refresh gesture lives once in the shell (MasterLayout), but the
 * actual "reload this screen's data" action is page-specific. Pages register
 * their fetch fn here; the shell invokes whatever is currently registered.
 */
interface RefreshContextType {
  /** Register a handler; returns a cleanup that deregisters it. */
  register: (handler: RefreshHandler) => () => void;
  /** Run every registered handler and resolve when all have settled. */
  runRefresh: () => Promise<void>;
}

// Create context
const RefreshContext = createContext<RefreshContextType | undefined>(undefined);

/**
 * Low-level hook for consuming the refresh registry (used by the shell).
 * @returns The refresh context.
 */
export const useRefreshRegistry = (): RefreshContextType => {
  const context = useContext(RefreshContext);
  if (!context) {
    throw new Error('useRefreshRegistry must be used within a RefreshProvider');
  }
  return context;
};

/**
 * Page-facing hook: registers this page's refresh handler for the duration it is
 * mounted. Call once in a page component with its (memoized) fetch function:
 *
 *   usePageRefresh(fetchOrders);
 *
 * The latest handler is always used (kept in a ref) without re-registering on
 * every render.
 */
export const usePageRefresh = (handler: RefreshHandler): void => {
  const { register } = useRefreshRegistry();
  const handlerRef = useRef<RefreshHandler>(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const stableHandler: RefreshHandler = () => handlerRef.current();
    return register(stableHandler);
  }, [register]);
};

/**
 * Refresh Provider Component
 * Holds the set of active page refresh handlers and exposes runRefresh().
 */
export const RefreshProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const handlersRef = useRef<Set<RefreshHandler>>(new Set());

  const register = useCallback((handler: RefreshHandler): (() => void) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const runRefresh = useCallback(async (): Promise<void> => {
    const handlers = Array.from(handlersRef.current);
    await Promise.all(
      handlers.map((handler) => Promise.resolve(handler()).catch(() => undefined))
    );
  }, []);

  const value: RefreshContextType = { register, runRefresh };

  return <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>;
};
